<template>
  <section>
    <box-default>
      <waiting-list :loading-content="loading">

        <!-- Barra de filtros + gatilhos -->
        <div class="ph-toolbar">
          <div class="ph-filters">
            <label>Período
              <select v-model.number="monthsBack" @change="loadDashboard">
                <option :value="1">Mês atual + anterior</option>
                <option :value="2">Últimos 3 meses</option>
                <option :value="5">Últimos 6 meses</option>
                <option :value="11">Últimos 12 meses</option>
              </select>
            </label>
            <label>À frente
              <select v-model.number="monthsForward" @change="loadDashboard">
                <option :value="0">Nenhum</option>
                <option :value="3">+3 meses</option>
                <option :value="6">+6 meses</option>
                <option :value="12">+12 meses</option>
              </select>
            </label>
            <label>Mês
              <select v-model.number="selectedYm" @change="onMonthChange">
                <option v-for="m in monthOptions" :key="m.yearMonth" :value="m.yearMonth">{{ m.label }}</option>
              </select>
            </label>
            <label class="ph-check"><input type="checkbox" v-model="showSubscription"/> Dados da assinatura</label>
            <label class="ph-check"><input type="checkbox" v-model="showCompany"/> Dados da empresa</label>
          </div>

          <div class="ph-triggers">
            <button class="ph-btn" :disabled="!!triggering" @click="trigger('snapshot')">
              <span v-if="triggering==='snapshot'">Enfileirando...</span><span v-else>Recalcular</span>
            </button>
            <button class="ph-btn" :disabled="!!triggering" @click="trigger('iugu')">Reconciliar IUGU</button>
            <button class="ph-btn" :disabled="!!triggering" @click="trigger('asaas')">Atualizar ASAAS</button>
            <button class="ph-btn" :disabled="!!triggering" @click="trigger('invoices')">Gerar NF</button>
          </div>
        </div>

        <p class="ph-hint">Passe o mouse em cada card para entender o que significa. Clique num card para ver a lista detalhada.</p>

        <!-- KPIs (clicáveis + tooltip) -->
        <div class="ph-cards">
          <div v-for="(k, i) in kpis" :key="i" class="ph-card" :class="{ clickable: !!k.kind }"
               :title="k.tip" @click="openDetail(k)">
            <div class="ph-card-title">{{ k.label }} <span class="ph-info">?</span></div>
            <div class="ph-card-value">{{ k.value }}</div>
          </div>
        </div>

        <!-- Gráficos -->
        <div class="ph-charts">
          <div class="ph-chart">
            <div class="ph-chart-title">Por tipo (qtd)</div>
            <div v-for="(b, i) in byTypeChart" :key="'t'+i" class="ph-bar-row" @click="openDetail({ label: 'Tipo: ' + b.name, kind: 'boletos', p: { type: b.type } })">
              <span class="ph-bar-label" :title="b.name">{{ b.name }}</span>
              <span class="ph-bar-track"><span class="ph-bar-fill" :style="{ width: b.pct + '%' }"></span></span>
              <span class="ph-bar-val">{{ b.count }}</span>
            </div>
          </div>
          <div class="ph-chart">
            <div class="ph-chart-title">Por origem (R$ pago)</div>
            <div class="ph-donut-wrap" v-if="originDonut.length">
              <svg viewBox="0 0 160 160" class="ph-donut">
                <g transform="translate(80,80) rotate(-90)">
                  <circle v-for="(s, i) in originDonut" :key="i" r="60" fill="none" :stroke="s.color"
                          stroke-width="22" :stroke-dasharray="s.dash + ' ' + s.circ" :stroke-dashoffset="-s.acc"/>
                </g>
              </svg>
              <div class="ph-donut-legend">
                <div v-for="(s, i) in originDonut" :key="'l'+i" class="ph-legend-item"
                     @click="openDetail({ label: 'Origem: ' + s.name, kind: 'boletos', p: { origin: s.origin } })">
                  <span class="ph-legend-dot" :style="{ background: s.color }"></span>
                  {{ s.name }} — R$ {{ fmtMoney(s.value) }} ({{ s.pct }}%)
                </div>
              </div>
            </div>
            <div v-else class="ph-empty">Sem dados</div>
          </div>
          <div class="ph-chart">
            <div class="ph-chart-title">Por status (qtd)</div>
            <div v-for="(b, i) in statusChart" :key="'s'+i" class="ph-bar-row">
              <span class="ph-bar-label" :title="b.name">{{ b.name }}</span>
              <span class="ph-bar-track"><span class="ph-bar-fill blue" :style="{ width: b.pct + '%' }"></span></span>
              <span class="ph-bar-val">{{ b.count }}</span>
            </div>
          </div>
        </div>

        <!-- Timeline diária do mês: Vencimento × Pago × Pago atrasado -->
        <div class="ph-chart ph-timeline">
          <div class="ph-chart-title ph-collapse" @click="showTimeline = !showTimeline">
            <span class="ph-caret">{{ showTimeline ? '▾' : '▸' }}</span>
            Timeline diária — Vencimento × Pago (por dia do mês selecionado)
          </div>
          <div v-if="showTimeline">
            <div class="ph-tl-controls">
              <label>Tipo
                <select v-model.number="timelineType">
                  <option :value="-1">Todos</option>
                  <option v-for="t in timelineTypeOptions" :key="'tt'+t.value" :value="t.value">{{ t.name }}</option>
                </select>
              </label>
              <label>Origem
                <select v-model.number="timelineOrigin">
                  <option :value="-1">Todas</option>
                  <option v-for="o in timelineOriginOptions" :key="'to'+o.value" :value="o.value">{{ o.name }}</option>
                </select>
              </label>
              <span class="ph-legend-inline">
                <span class="ph-legend-dot" style="background:#f39c12"></span>Vencimento (R$ {{ fmtMoney(timeline.totVenc) }})
                <span class="ph-legend-dot" style="background:#00A651"></span>Pago (R$ {{ fmtMoney(timeline.totPago) }})
                <span class="ph-legend-dot" style="background:#e74c3c"></span>Pago atrasado (R$ {{ fmtMoney(timeline.totAtras) }})
              </span>
              <span v-if="loadingTimeline" class="ph-tl-loading">carregando…</span>
            </div>
            <svg v-if="timeline.n && monthBoletos.length" :viewBox="'0 0 ' + timeline.W + ' ' + timeline.H" class="ph-line ph-line-tall">
              <line v-for="(g, i) in timeline.grid" :key="'g'+i" :x1="timeline.padL" :y1="g.y" :x2="timeline.W - timeline.padR" :y2="g.y" class="ph-grid"/>
              <text v-for="(g, i) in timeline.grid" :key="'gt'+i" :x="timeline.padL - 6" :y="g.y + 3" text-anchor="end" class="ph-axis">{{ g.label }}</text>
              <polyline :points="timeline.vencPts" fill="none" stroke="#f39c12" stroke-width="2"/>
              <polyline :points="timeline.pagoPts" fill="none" stroke="#00A651" stroke-width="2"/>
              <polyline :points="timeline.atrasPts" fill="none" stroke="#e74c3c" stroke-width="2" stroke-dasharray="4 3"/>
              <circle v-for="(d, i) in timeline.vencDots" :key="'vd'+i" :cx="d.x" :cy="d.y" r="2.5" fill="#f39c12"><title>Vencimento {{ d.t }}</title></circle>
              <circle v-for="(d, i) in timeline.pagoDots" :key="'pd'+i" :cx="d.x" :cy="d.y" r="2.5" fill="#00A651"><title>Pago {{ d.t }}</title></circle>
              <circle v-for="(d, i) in timeline.atrasDots" :key="'ad'+i" :cx="d.x" :cy="d.y" r="2.5" fill="#e74c3c"><title>Pago atrasado {{ d.t }}</title></circle>
              <text v-for="(t, i) in timeline.xLabels" :key="'xl'+i" :x="t.x" :y="timeline.H - 6" text-anchor="middle" class="ph-axis">{{ t.label }}</text>
            </svg>
            <div v-else-if="!loadingTimeline" class="ph-empty">Sem dados no mês</div>
          </div>
        </div>

        <!-- Painel de detalhe (drill-down) -->
        <div v-if="detail.open" class="ph-detail">
          <div class="ph-detail-head">
            <strong>{{ detail.title }} ({{ detail.totalItems }})</strong>
            <div>
              <template v-if="selectedRows.length">
                <span class="ph-sel-count">{{ selectedRows.length }} selec.</span>
                <button class="ph-btn small" :disabled="reprocessing" @click="reprocessSelected">
                  <span v-if="reprocessing">Reprocessando…</span><span v-else>Reprocessar NF</span>
                </button>
                <button class="ph-btn small grey" @click="clearRowSelection">Limpar seleção</button>
              </template>
              <button class="ph-btn small" @click="excelDetail">Gerar Excel</button>
              <button class="ph-btn small grey" @click="detail.open = false">Fechar</button>
            </div>
          </div>
          <waiting-list :loading-content="loadingDetail">

            <!-- NF em erro (mensagem da Focus) -->
            <div v-if="detail.kind === 'nferrors'" class="ph-table-wrap">
              <table class="ph-table">
                <thead><tr>
                  <th><input type="checkbox" :checked="allNfErrorsSelected" @change="toggleAllNfErrors($event.target.checked)" title="Selecionar todas"/></th>
                  <th>Empresa</th><th>CNPJ</th><th>Status Focus</th><th>Mensagem</th><th>Ref</th><th>Data</th><th>Ação</th>
                </tr></thead>
                <tbody>
                  <tr v-for="(e, i) in detail.items" :key="i">
                    <td><input type="checkbox" :checked="isRowSelected(e.invoiceId)" :disabled="!e.invoiceId" @change="toggleRow(e.invoiceId, e.bankCode)"/></td>
                    <td>{{ e.companyName }}</td><td>{{ e.cnpj }}</td><td>{{ e.focusStatus }}</td>
                    <td class="ph-msg">{{ nfErrorMessage(e) }}</td><td>{{ e.invoiceId }}</td><td>{{ e.createdAt | formatDate }}</td>
                    <td><button class="ph-btn small" :disabled="!e.invoiceId" @click="consultFocus(e.invoiceId, e.bankCode)">Consultar Focus</button></td>
                  </tr>
                  <tr v-if="!detail.items.length"><td colspan="8" class="ph-empty">Nada por aqui 🎉</td></tr>
                </tbody>
              </table>
            </div>

            <!-- Boletos detalhados -->
            <div v-else class="ph-detail-body">
              <div v-if="detail.items.length" class="ph-filterbar">
                <div v-for="col in filterCols" :key="col.key" class="ph-filter">
                  <button class="ph-filter-btn" :class="{ active: (boletoFilters[col.key] || []).length }" @click="toggleFilterPanel(col.key)">
                    {{ col.label }}<span v-if="(boletoFilters[col.key] || []).length">&nbsp;({{ boletoFilters[col.key].length }})</span> ▾
                  </button>
                  <div v-if="openFilterKey === col.key" class="ph-filter-pop">
                    <div class="ph-filter-pop-head">
                      <span>{{ col.label }}</span>
                      <a class="link" @click="clearFilterKey(col.key)">limpar</a>
                    </div>
                    <div class="ph-filter-opts">
                      <label v-for="val in filterValues(col)" :key="val" class="ph-filter-opt">
                        <input type="checkbox" :checked="isSel(col.key, val)" @change="toggleVal(col.key, val)"/>
                        <span>{{ val }}</span>
                      </label>
                    </div>
                  </div>
                </div>
                <button v-if="anyFilterActive" class="ph-btn small grey" @click="clearAllFilters">Limpar filtros</button>
                <span class="ph-filter-count">{{ filteredBoletos.length }} de {{ detail.items.length }}</span>
              </div>
              <div class="ph-table-wrap">
              <table class="ph-table">
                <thead>
                  <tr>
                    <th><input type="checkbox" :checked="allBoletosSelected" @change="toggleAllBoletos($event.target.checked)" title="Selecionar todas"/></th>
                    <th>Empresa</th><th>Inadimplente</th><th>Origem</th><th>Tipo</th><th>Status pag.</th>
                    <th>Vencimento</th><th>Pago em</th><th>Valor</th><th>Pago</th><th>NF</th><th>Ação</th>
                    <template v-if="showSubscription"><th>Plano</th><th>Validade</th><th>MRR</th><th>Status assin.</th></template>
                    <template v-if="showCompany"><th>CNPJ</th><th>E-mail fin.</th><th>Telefone</th></template>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(b, i) in filteredBoletos" :key="i">
                    <td><input type="checkbox" :checked="isRowSelected(b.id)" :disabled="!b.id" @change="toggleRow(b.id, b.contextCode)"/></td>
                    <td><a :href="'/detalhes-da-empresa/' + b.companyId + '/' + b.contextCode" target="_blank" class="link">{{ b.companyName || '(sem empresa)' }}</a></td>
                    <td><span v-if="b.default" class="ph-badge-danger">Inadimplente</span><span v-else class="ph-muted">—</span></td>
                    <td>{{ b.originName }}</td>
                    <td>{{ b.typeName }}</td>
                    <td>{{ b.paymentStatusName }}</td>
                    <td>{{ b.expireAt | formatDate }}</td>
                    <td><span v-if="b.paidAt">{{ b.paidAt | formatDate }}</span><span v-else>-</span></td>
                    <td>R$ {{ fmtMoney(b.totalCost) }}</td>
                    <td :title="paidTitle(b)">{{ paidLabel(b) }}</td>
                    <td>
                      <a v-if="b.invoiceUri" :href="b.invoiceUri" target="_blank" class="link">{{ b.invoiceStatusName }}</a>
                      <span v-else>{{ b.invoiceGenerated ? b.invoiceStatusName : 'Não gerada' }}</span>
                    </td>
                    <td><button v-if="b.id" class="ph-btn small" @click="consultFocus(b.id, b.contextCode)">Consultar Focus</button><span v-else class="ph-muted">—</span></td>
                    <template v-if="showSubscription">
                      <td>{{ b.planTypeName }}</td>
                      <td><span v-if="b.contractValidUntil">{{ b.contractValidUntil | formatDate }}</span><span v-else>-</span></td>
                      <td>R$ {{ fmtMoney(b.subscriptionTotalCost) }}</td>
                      <td>{{ b.subscriptionStatusName }}</td>
                    </template>
                    <template v-if="showCompany">
                      <td>{{ b.cnpj }}</td>
                      <td>{{ b.companyFinancialEmail || b.companyEmail }}</td>
                      <td>{{ b.companyCellPhone || b.companyPhone }}</td>
                    </template>
                  </tr>
                  <tr v-if="!filteredBoletos.length"><td colspan="19" class="ph-empty">Nada por aqui 🎉</td></tr>
                </tbody>
              </table>
              </div>
            </div>

          </waiting-list>
        </div>

      </waiting-list>
    </box-default>

    <!-- Modal: consulta Focus / erro da NF (item 1) -->
    <div v-if="focusModal.open" class="ph-modal-overlay" @click.self="focusModal.open = false">
      <div class="ph-modal">
        <div class="ph-modal-head">
          <strong>Consulta Focus / NF</strong>
          <button class="ph-btn small grey" @click="focusModal.open = false">Fechar</button>
        </div>
        <div v-if="focusModal.loading" class="ph-modal-body">Consultando a Focus…</div>
        <div v-else-if="focusModal.data" class="ph-modal-body">
          <p>
            <strong>Empresa:</strong> {{ focusModal.data.companyName || '-' }}
            &nbsp;·&nbsp; <strong>CNPJ:</strong> {{ focusModal.data.cnpj || '-' }}
          </p>
          <p>
            <strong>Status na Focus:</strong>
            <span class="ph-badge" :class="focusStatusClass(focusModal.data.result)">{{ focusStatusLabel(focusModal.data) }}</span>
            <a v-if="focusModal.data.invoiceUri" :href="focusModal.data.invoiceUri" target="_blank" class="link">abrir NF</a>
          </p>
          <p v-if="focusModal.data.result === 'processing'" class="ph-muted">A nota está em processamento na Focus. Tente novamente em alguns minutos.</p>

          <div v-if="focusModal.data.errorMessage">
            <label class="ph-modal-label">Erro retornado</label>
            <pre class="ph-pre">{{ focusModal.data.errorMessage }}</pre>
          </div>

          <label class="ph-modal-label">Endpoints</label>
          <pre class="ph-pre">{{ focusModal.data.endpointEmit }}
{{ focusModal.data.endpointConsult }}</pre>

          <label class="ph-modal-label">Payload (dados enviados)</label>
          <pre class="ph-pre">{{ focusModal.data.payloadPreview }}</pre>

          <label class="ph-modal-label">E-mail pronto para a Focus</label>
          <pre class="ph-pre">{{ focusModal.data.emailText }}</pre>
        </div>
        <div class="ph-modal-foot">
          <button class="ph-btn small" :disabled="!focusModal.data" @click="copyText(focusModal.data && focusModal.data.emailText)">Copiar e-mail</button>
          <button class="ph-btn small" :disabled="!focusModal.data" @click="copyText(focusModal.data && focusModal.data.payloadPreview)">Copiar payload</button>
          <button class="ph-btn small grey" @click="focusModal.open = false">Fechar</button>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import topMenu from "../../components/default/topMenu.vue";
import fullLoading from "../../components/default/fullLoading.vue";
import {
  PAYMENT_HEALTH_DASH,
  PAYMENT_HEALTH_BOLETOS,
  PAYMENT_HEALTH_NF_ERRORS,
  PAYMENT_HEALTH_TRIGGER_IUGU,
  PAYMENT_HEALTH_TRIGGER_ASAAS,
  PAYMENT_HEALTH_TRIGGER_INVOICES,
  PAYMENT_HEALTH_TRIGGER_SNAPSHOT,
  PAYMENT_HEALTH_NF_CONSULT,
  PAYMENT_HEALTH_NF_REPROCESS
} from "../../../../urlConfig";
import {REQUEST_SERVICE} from "../../../../requestConfig";
import {UTIL, UTIL_SERVICE} from "../../../../utils";
import {ALERT_SERVICE} from "../../../../msgAlertService";
import BoxDefault from "../../components/layouts/boxDefault.vue";
import WaitingList from "../../components/layouts/waitingList.vue";

export default {
  name: "paymentHealthcheck",
  components: {WaitingList, BoxDefault, topMenu, fullLoading},
  data() {
    return {
      loading: false,
      loadingDetail: false,
      triggering: "",
      monthsBack: 1,
      monthsForward: 6,
      selectedYm: null,
      dash: {months: []},
      showSubscription: false,
      showCompany: false,
      showTimeline: false,
      detail: {open: false, kind: "boletos", title: "", items: [], totalItems: 0, params: {}},
      boletoFilters: {},
      openFilterKey: null,
      monthBoletos: [],
      loadingTimeline: false,
      timelineType: -1,
      timelineOrigin: -1,
      unlocked: false,
      selectedRows: [],
      reprocessing: false,
      focusModal: {open: false, loading: false, data: null},
    };
  },
  computed: {
    filterCols() {
      const ym = (d) => {
        if (!d) return "—";
        const dt = new Date(d);
        if (isNaN(dt.getTime())) return "—";
        return ("0" + (dt.getMonth() + 1)).slice(-2) + "/" + dt.getFullYear();
      };
      const cols = [
        {key: "companyName", label: "Empresa", get: b => b.companyName || "(sem empresa)"},
        {key: "default", label: "Inadimplente", get: b => (b.default ? "Sim" : "Não")},
        {key: "originName", label: "Origem", get: b => b.originName},
        {key: "typeName", label: "Tipo", get: b => b.typeName},
        {key: "paymentStatusName", label: "Status pag.", get: b => b.paymentStatusName},
        {key: "nf", label: "NF", get: b => (b.invoiceGenerated ? (b.invoiceStatusName || "Gerada") : "Não gerada")},
        {key: "expireMonth", label: "Vencimento", get: b => ym(b.expireAt)},
        {key: "totalCost", label: "Valor", get: b => "R$ " + this.fmtMoney(b.totalCost)},
      ];
      if (this.showSubscription) {
        cols.push({key: "planTypeName", label: "Plano", get: b => b.planTypeName});
        cols.push({key: "subscriptionStatusName", label: "Status assin.", get: b => b.subscriptionStatusName});
      }
      if (this.showCompany) {
        cols.push({key: "cnpj", label: "CNPJ", get: b => b.cnpj});
      }
      return cols;
    },
    anyFilterActive() {
      return Object.keys(this.boletoFilters).some(k => (this.boletoFilters[k] || []).length > 0);
    },
    filteredBoletos() {
      const items = this.detail.items || [];
      if (!this.anyFilterActive) return items;
      const cols = this.filterCols;
      return items.filter(b => {
        for (let ci = 0; ci < cols.length; ci++) {
          const col = cols[ci];
          const sel = this.boletoFilters[col.key];
          if (sel && sel.length && sel.indexOf(col.get(b)) === -1) return false;
        }
        return true;
      });
    },
    month() {
      if (!this.dash.months || !this.dash.months.length) return {};
      // Mês sem snapshot (ex.: futuro/parcelamento) -> KPIs zerados (honesto); o drill-down é consulta ao vivo.
      return this.dash.months.find(m => m.yearMonth === this.selectedYm) || {};
    },
    // Opções do seletor de mês: une os meses com snapshot com o intervalo (passado + atual + futuro),
    // para permitir navegar em meses futuros (parcelamentos) mesmo sem snapshot ainda.
    monthOptions() {
      const map = {};
      (this.dash.months || []).forEach(m => { map[m.yearMonth] = {yearMonth: m.yearMonth, label: m.label}; });
      const now = new Date();
      const start = new Date(now.getFullYear(), now.getMonth() - this.monthsBack, 1);
      const end = new Date(now.getFullYear(), now.getMonth() + this.monthsForward, 1);
      const d = new Date(start);
      while (d <= end) {
        const ym = d.getFullYear() * 100 + (d.getMonth() + 1);
        if (!map[ym]) map[ym] = {yearMonth: ym, label: this.ymLabel(ym)};
        d.setMonth(d.getMonth() + 1);
      }
      return Object.keys(map).map(k => map[k]).sort((a, b) => a.yearMonth - b.yearMonth);
    },
    allBoletosSelected() {
      const rows = (this.filteredBoletos || []).filter(b => b.id);
      return rows.length > 0 && rows.every(b => this.isRowSelected(b.id));
    },
    allNfErrorsSelected() {
      const rows = (this.detail.items || []).filter(e => e.invoiceId);
      return rows.length > 0 && rows.every(e => this.isRowSelected(e.invoiceId));
    },
    kpis() {
      const m = this.month || {};
      const cm = (c, a) => (c || 0) + " • R$ " + this.fmtMoney(a);
      return [
        {label: "Boletos gerados", value: cm(m.generatedCount, m.generatedAmount), tip: "Todos os boletos que VENCEM no mês (qualquer status).", kind: "boletos", p: {}},
        {label: "Pago (de fato)", value: cm(m.paidCount, m.paidAmount), tip: "Boletos pagos e o valor efetivamente recebido no mês.", kind: "boletos", p: {status: 1}},
        {label: "Pendente", value: cm(m.pendingCount, m.pendingAmount), tip: "Boletos ainda NÃO pagos (aguardando pagamento).", kind: "boletos", p: {status: 0}},
        {label: "Expirado", value: cm(m.expiredCount, m.expiredAmount), tip: "Boletos que expiraram sem pagamento.", kind: "boletos", p: {status: 3}},
        {label: "Parcial", value: cm(m.partiallyPaidCount, m.partiallyPaidAmount), tip: "Boletos pagos parcialmente (qtd • valor pago).", kind: "boletos", p: {status: 6}},
        {label: "Cancelado / Reembolsado", value: cm(m.refundedCanceledCount, m.refundedCanceledAmount), tip: "Boletos cancelados ou reembolsados (qtd • valor).", kind: "boletos", p: {status: 2}},
        {label: "NF geradas", value: cm(m.nfGenerated, m.nfGeneratedAmount), tip: "Boletos com nota fiscal já emitida (qtd • valor faturado).", kind: null, p: {}},
        {label: "Pago SEM NF", value: cm(m.nfMissingPaid, m.nfMissingPaidAmount), tip: "Boletos PAGOS que ainda NÃO têm nota fiscal — precisam gerar NF (qtd • valor pago).", kind: "boletos", p: {special: "missingnf"}},
        {label: "NF em erro (Focus)", value: String(m.nfError || 0), tip: "Notas recusadas pela Focus (erro de autorização). Clique para ver a mensagem real.", kind: "nferrors", p: {}},
        {label: "Drift IUGU", value: String(m.driftIugu || 0), tip: "Boletos IUGU vencidos e ainda pendentes aqui — pode ser pagamento não atualizado. Rode 'Reconciliar IUGU'.", kind: "boletos", p: {special: "driftiugu"}},
        {label: "Drift ASAAS", value: String(m.driftAsaas || 0), tip: "Boletos ASAAS vencidos e ainda pendentes aqui. Rode 'Atualizar ASAAS'.", kind: "boletos", p: {special: "driftasaas"}},
      ];
    },
    byTypeChart() {
      const arr = (this.month.byType || []).slice().sort((a, b) => b.count - a.count);
      const max = Math.max(1, ...arr.map(x => x.count));
      return arr.map(x => ({type: x.type, name: x.typeName, count: x.count, pct: Math.round(x.count / max * 100)}));
    },
    originChart() {
      const arr = (this.month.byOrigin || []);
      const max = Math.max(1, ...arr.map(x => x.totalPaid));
      return arr.map(x => ({origin: x.origin, name: x.originName, paid: x.totalPaid, pct: Math.round(x.totalPaid / max * 100)}));
    },
    statusChart() {
      const arr = (this.month.statusTotals || []).slice().sort((a, b) => b.count - a.count);
      const max = Math.max(1, ...arr.map(x => x.count));
      return arr.map(x => ({name: x.statusName, count: x.count, pct: Math.round(x.count / max * 100)}));
    },
    originDonut() {
      const arr = (this.month.byOrigin || []).filter(x => x.totalPaid > 0);
      const total = arr.reduce((s, x) => s + x.totalPaid, 0) || 1;
      const colors = ["#00A651", "#f39c12", "#2980b9", "#8e44ad", "#999"];
      const circ = 2 * Math.PI * 60;
      let acc = 0;
      return arr.map((x, i) => {
        const frac = x.totalPaid / total;
        const dash = frac * circ;
        const seg = {origin: x.origin, name: x.originName, value: x.totalPaid, pct: Math.round(frac * 100), color: colors[i % colors.length], dash: dash, circ: circ, acc: acc};
        acc += dash;
        return seg;
      });
    },
    timelineTypeOptions() {
      const seen = {}, out = [];
      (this.monthBoletos || []).forEach(b => {
        if (b.type != null && !seen["t" + b.type]) { seen["t" + b.type] = true; out.push({value: b.type, name: b.typeName || ("Tipo " + b.type)}); }
      });
      return out.sort((a, b) => a.name.localeCompare(b.name, "pt-BR"));
    },
    timelineOriginOptions() {
      const seen = {}, out = [];
      (this.monthBoletos || []).forEach(b => {
        if (b.origin != null && !seen["o" + b.origin]) { seen["o" + b.origin] = true; out.push({value: b.origin, name: b.originName || ("Origem " + b.origin)}); }
      });
      return out.sort((a, b) => a.name.localeCompare(b.name, "pt-BR"));
    },
    timelineBoletos() {
      let arr = this.monthBoletos || [];
      if (this.timelineType >= 0) arr = arr.filter(b => b.type === this.timelineType);
      if (this.timelineOrigin >= 0) arr = arr.filter(b => b.origin === this.timelineOrigin);
      return arr;
    },
    timeline() {
      const ym = this.selectedYm;
      if (!ym) return {n: 0};
      const year = Math.floor(ym / 100), month = ym % 100;
      const days = new Date(year, month, 0).getDate();
      const venc = new Array(days + 1).fill(0);
      const pago = new Array(days + 1).fill(0);
      const atras = new Array(days + 1).fill(0);
      // Parseia só a parte da data (YYYY-MM-DD) ao meio-dia local p/ não deslocar o dia por fuso.
      const parseDay = s => s ? new Date(("" + s).substring(0, 10) + "T12:00:00") : null;
      const inMonth = d => d && d.getFullYear() === year && (d.getMonth() + 1) === month;
      (this.timelineBoletos || []).forEach(b => {
        const exp = parseDay(b.expireAt);
        const paid = parseDay(b.paidAt);
        const val = b.totalCost || 0;
        const paidVal = (b.totalPaid != null ? b.totalPaid : (b.paymentStatus === 1 ? val : 0)) || 0;
        if (inMonth(exp)) venc[exp.getDate()] += val;
        if (inMonth(paid)) {
          pago[paid.getDate()] += paidVal;
          if (exp && paid > exp) atras[paid.getDate()] += paidVal;
        }
      });
      const W = 900, H = 220, padL = 56, padR = 14, padT = 14, padB = 26;
      const max = Math.max(1, ...venc, ...pago, ...atras);
      const x = d => padL + (days <= 1 ? 0 : (d - 1) * (W - padL - padR) / (days - 1));
      const y = v => padT + (H - padT - padB) * (1 - v / max);
      const ptsOf = arr => {
        let s = "";
        for (let d = 1; d <= days; d++) s += (s ? " " : "") + x(d) + "," + y(arr[d]);
        return s;
      };
      const dotsOf = arr => {
        const o = [];
        for (let d = 1; d <= days; d++) o.push({x: x(d), y: y(arr[d]), t: d + "/" + month + " — R$ " + this.fmtMoney(arr[d])});
        return o;
      };
      const fmtK = v => v >= 1000 ? (v / 1000).toFixed(0) + "k" : String(Math.round(v));
      const grid = [0, 0.25, 0.5, 0.75, 1].map(f => ({y: y(max * f), label: "R$ " + fmtK(max * f)}));
      const xLabels = [];
      for (let d = 1; d <= days; d++) if (d === 1 || d === days || d % 5 === 0) xLabels.push({x: x(d), label: String(d)});
      const tot = arr => arr.reduce((s, v) => s + v, 0);
      return {
        n: days, W: W, H: H, padL: padL, padR: padR,
        vencPts: ptsOf(venc), pagoPts: ptsOf(pago), atrasPts: ptsOf(atras),
        vencDots: dotsOf(venc), pagoDots: dotsOf(pago), atrasDots: dotsOf(atras),
        grid: grid, xLabels: xLabels,
        totVenc: tot(venc), totPago: tot(pago), totAtras: tot(atras)
      };
    }
  },
  methods: {
    filterValues(col) {
      const seen = {};
      const out = [];
      (this.detail.items || []).forEach(b => {
        const v = col.get(b);
        if (!seen[v]) {
          seen[v] = true;
          out.push(v);
        }
      });
      return out.sort((a, b) => ("" + a).localeCompare("" + b, "pt-BR", {numeric: true}));
    },
    toggleFilterPanel(key) {
      this.openFilterKey = this.openFilterKey === key ? null : key;
    },
    isSel(key, val) {
      const a = this.boletoFilters[key];
      return !!a && a.indexOf(val) !== -1;
    },
    toggleVal(key, val) {
      const a = this.boletoFilters[key] ? this.boletoFilters[key].slice() : [];
      const i = a.indexOf(val);
      if (i === -1) a.push(val);
      else a.splice(i, 1);
      this.$set(this.boletoFilters, key, a);
    },
    clearFilterKey(key) {
      this.$set(this.boletoFilters, key, []);
    },
    clearAllFilters() {
      this.boletoFilters = {};
      this.openFilterKey = null;
    },
    hasPerm() {
      try {
        return UTIL.checkP(JSON.parse(window.localStorage.getItem("mamUserMasterInfo")).id);
      } catch (e) {
        return false;
      }
    },
    fmtMoney(v) {
      return Number(v || 0).toLocaleString("pt-BR", {minimumFractionDigits: 2, maximumFractionDigits: 2});
    },
    // Pago (status=1) sem TotalPaid registrado: assume o valor do boleto (mesma regra do KPI).
    paidValue(b) {
      if (b.totalPaid != null) return b.totalPaid;
      if (b.paymentStatus === 1) return b.totalCost;
      return null;
    },
    paidLabel(b) {
      const v = this.paidValue(b);
      if (v == null) return "-";
      const assumed = (b.totalPaid == null && b.paymentStatus === 1) ? " *" : "";
      return "R$ " + this.fmtMoney(v) + assumed;
    },
    paidTitle(b) {
      return (b.totalPaid == null && b.paymentStatus === 1)
        ? "* Pago sem valor registrado — assumido o valor do boleto"
        : "";
    },
    ymParts(ym) {
      return {year: Math.floor(ym / 100), month: ym % 100};
    },
    ymLabel(ym) {
      const y = Math.floor(ym / 100), mo = ym % 100;
      const names = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];
      return (names[mo - 1] || mo) + "/" + y;
    },
    computeRange() {
      const now = new Date();
      // "to" avança monthsForward para incluir competências futuras (parcelamentos) no snapshot quando houver.
      const toDate = new Date(now.getFullYear(), now.getMonth() + this.monthsForward, 1);
      const to = toDate.getFullYear() * 100 + (toDate.getMonth() + 1);
      const d = new Date(now.getFullYear(), now.getMonth() - this.monthsBack, 1);
      const from = d.getFullYear() * 100 + (d.getMonth() + 1);
      return {from, to};
    },
    loadDashboard() {
      if (!this.hasPerm()) return;
      this.loading = true;
      this.detail.open = false;
      const {from, to} = this.computeRange();
      const url = `${PAYMENT_HEALTH_DASH}?fromYearMonth=${from}&toYearMonth=${to}`;
      REQUEST_SERVICE.get(url).then((response) => {
        UTIL_SERVICE.console("PAYMENT_HEALTH_DASH:", response.data);
        this.dash = response.data || {months: []};
        // Seleciona o mês ATUAL por padrão (não o último do range, que pode ser futuro).
        const now = new Date();
        this.selectedYm = now.getFullYear() * 100 + (now.getMonth() + 1);
        this.loading = false;
        this.loadTimelineData();
      }).catch((reason) => {
        this.loading = false;
        this.handleError(reason);
      });
    },
    loadTimelineData() {
      if (!this.selectedYm) return;
      const {year, month} = this.ymParts(this.selectedYm);
      this.loadingTimeline = true;
      const url = `${PAYMENT_HEALTH_BOLETOS}?year=${year}&month=${month}&status=-1&type=-1&origin=-1&special=&page=1&qtd=100000`;
      REQUEST_SERVICE.get(url).then((r) => {
        this.monthBoletos = (r.data && r.data.items) || [];
        this.loadingTimeline = false;
      }).catch(() => {
        this.monthBoletos = [];
        this.loadingTimeline = false;
      });
    },
    onMonthChange() {
      this.loadTimelineData();
      if (this.detail.open) this.reloadDetail();
    },
    openDetail(card) {
      if (!card || !card.kind) return;
      this.detail.title = card.label;
      this.detail.kind = card.kind;
      this.detail.params = card.p || {};
      this.detail.open = true;
      this.reloadDetail();
    },
    reloadDetail() {
      if (this.detail.kind === "nferrors") this.loadNfErrors();
      else this.loadBoletos(this.detail.params || {});
    },
    loadBoletos(p) {
      if (!this.selectedYm) return;
      const {year, month} = this.ymParts(this.selectedYm);
      this.loadingDetail = true;
      this.boletoFilters = {};
      this.openFilterKey = null;
      this.selectedRows = [];
      const status = p.status != null ? p.status : -1;
      const type = p.type != null ? p.type : -1;
      const origin = p.origin != null ? p.origin : -1;
      const special = p.special || "";
      const url = `${PAYMENT_HEALTH_BOLETOS}?year=${year}&month=${month}&status=${status}&type=${type}&origin=${origin}&special=${special}&page=1&qtd=100000`;
      REQUEST_SERVICE.get(url).then((r) => {
        this.detail.items = (r.data && r.data.items) || [];
        this.detail.totalItems = (r.data && r.data.totalItems) || 0;
        this.loadingDetail = false;
      }).catch((e) => {
        this.loadingDetail = false;
        this.handleError(e);
      });
    },
    loadNfErrors() {
      if (!this.selectedYm) return;
      const {year, month} = this.ymParts(this.selectedYm);
      this.loadingDetail = true;
      this.selectedRows = [];
      const url = `${PAYMENT_HEALTH_NF_ERRORS}?year=${year}&month=${month}&page=1&qtd=100000`;
      REQUEST_SERVICE.get(url).then((r) => {
        this.detail.items = (r.data && r.data.items) || [];
        this.detail.totalItems = (r.data && r.data.totalItems) || 0;
        this.loadingDetail = false;
      }).catch((e) => {
        this.loadingDetail = false;
        this.handleError(e);
      });
    },
    nfErrorMessage(e) {
      if (e.focusMensagem) return e.focusMensagem;
      if (e.erros && e.erros.length) return e.erros.map(x => (x.codigo ? x.codigo + ": " : "") + (x.mensagem || "")).join(" | ");
      return e.rawResult || "";
    },
    trigger(kind) {
      const map = {
        snapshot: {url: PAYMENT_HEALTH_TRIGGER_SNAPSHOT, msg: "Recálculo enfileirado"},
        iugu: {url: PAYMENT_HEALTH_TRIGGER_IUGU, msg: "Reconciliação IUGU enfileirada"},
        asaas: {url: PAYMENT_HEALTH_TRIGGER_ASAAS, msg: "Atualização ASAAS enfileirada"},
        invoices: {url: PAYMENT_HEALTH_TRIGGER_INVOICES, msg: "Geração de NF enfileirada"},
      };
      const cfg = map[kind];
      if (!cfg) return;
      this.triggering = kind;
      REQUEST_SERVICE.post(cfg.url, {}).then(() => {
        this.triggering = "";
        ALERT_SERVICE.swal("Enfileirado", cfg.msg + ". Roda em segundo plano; atualize a página em alguns minutos.", "success");
      }).catch((reason) => {
        this.triggering = "";
        this.handleError(reason);
      });
    },
    handleError(reason) {
      let msg = "Não foi possível completar a operação.";
      try {
        if (reason && reason.response && reason.response.data) {
          const d = reason.response.data;
          if (d.erros && d.erros.length) msg = d.erros.join("\n");
          else if (typeof d === "string") msg = d;
        }
      } catch (e) {
      }
      ALERT_SERVICE.swal("Ops...", msg, "error");
    },
    excelDetail() {
      if (this.detail.kind === "nferrors") {
        const head = ["EMPRESA", "CNPJ", "STATUS FOCUS", "MENSAGEM", "REF", "DATA"];
        const body = this.detail.items.map(e => [e.companyName, e.cnpj, e.focusStatus, this.nfErrorMessage(e), e.invoiceId, e.createdAt]);
        window.downloadListExcel(head, body, "NFEmErro");
        return;
      }
      let head = ["EMPRESA", "INADIMPLENTE", "ORIGEM", "TIPO", "STATUS PAG.", "VENCIMENTO", "PAGO EM", "VALOR", "PAGO", "STATUS NF", "LINK NF"];
      if (this.showSubscription) head = head.concat(["PLANO", "VALIDADE", "MRR", "STATUS ASSINATURA", "MAX FUNC."]);
      if (this.showCompany) head = head.concat(["ID EMPRESA", "CNPJ", "EMAIL", "EMAIL FINANCEIRO", "TELEFONE", "CELULAR"]);
      const body = this.filteredBoletos.map(b => {
        let row = [b.companyName, b.default ? "Sim" : "Não", b.originName, b.typeName, b.paymentStatusName, b.expireAt, b.paidAt, b.totalCost, this.paidValue(b), b.invoiceStatusName, b.invoiceUri];
        if (this.showSubscription) row = row.concat([b.planTypeName, b.contractValidUntil, b.subscriptionTotalCost, b.subscriptionStatusName, b.maxTotalEmployeeInContract]);
        if (this.showCompany) row = row.concat([b.companyId, b.cnpj, b.companyEmail, b.companyFinancialEmail, b.companyPhone, b.companyCellPhone]);
        return row;
      });
      window.downloadListExcel(head, body, "BoletosDetalhado");
    },

    // ---- Seleção de linhas p/ reprocessamento (item 4) ----
    isRowSelected(id) {
      return this.selectedRows.some(r => r.id === id);
    },
    toggleRow(id, bank) {
      if (!id) return;
      const i = this.selectedRows.findIndex(r => r.id === id);
      if (i === -1) this.selectedRows.push({id: id, bank: bank});
      else this.selectedRows.splice(i, 1);
    },
    toggleAllBoletos(checked) {
      this.selectedRows = checked
        ? (this.filteredBoletos || []).filter(b => b.id).map(b => ({id: b.id, bank: b.contextCode}))
        : [];
    },
    toggleAllNfErrors(checked) {
      this.selectedRows = checked
        ? (this.detail.items || []).filter(e => e.invoiceId).map(e => ({id: e.invoiceId, bank: e.bankCode}))
        : [];
    },
    clearRowSelection() {
      this.selectedRows = [];
    },
    reprocessSelected() {
      if (!this.selectedRows.length) return;
      const n = this.selectedRows.length;
      if (n > 50) {
        ALERT_SERVICE.swal("Muitos itens", "Reprocessa no máximo 50 boletos por vez. Reduza a seleção.", "warning");
        return;
      }
      if (!window.confirm(`Reprocessar a NF de ${n} boleto(s)?\n\nIsso FORÇA a reemissão na Focus (respeitando a trava de mês). Boletos cuja competência esteja fora da janela permitida serão bloqueados.`)) return;
      this.reprocessing = true;
      REQUEST_SERVICE.post(PAYMENT_HEALTH_NF_REPROCESS, {items: this.selectedRows}).then((r) => {
        this.reprocessing = false;
        const d = r.data || {};
        const msg = `Reemitidas: ${d.reemitidas || 0}\nBloqueadas (trava de mês): ${d.bloqueadas || 0}\nNão encontradas: ${d.naoEncontradas || 0}\nFalhas: ${d.falhas || 0}`;
        ALERT_SERVICE.swal("Reprocessamento concluído", msg, (d.falhas ? "warning" : "success"));
        this.clearRowSelection();
        if (this.detail.open) this.reloadDetail();
      }).catch((reason) => {
        this.reprocessing = false;
        this.handleError(reason);
      });
    },

    // ---- Consulta viva à Focus + modal (item 1) ----
    consultFocus(id, bank) {
      if (!id || !bank) {
        ALERT_SERVICE.swal("Ops...", "Boleto sem Id/banco para consultar a Focus.", "error");
        return;
      }
      this.focusModal = {open: true, loading: true, data: null};
      REQUEST_SERVICE.post(PAYMENT_HEALTH_NF_CONSULT, {id: id, bank: bank}).then((r) => {
        const d = r.data || {};
        this.focusModal.loading = false;
        this.focusModal.data = d;
        if (d.result === "authorized" && d.synced) {
          this.focusModal.open = false;
          ALERT_SERVICE.swal("Sincronizada", "A NF estava autorizada na Focus e foi sincronizada aqui.", "success");
          if (this.detail.open) this.reloadDetail();
        }
      }).catch((reason) => {
        this.focusModal.loading = false;
        this.focusModal.open = false;
        this.handleError(reason);
      });
    },
    focusStatusLabel(d) {
      if (!d) return "";
      const map = {authorized: "Autorizada", processing: "Processando", error: "Erro de autorização", notfound: "Não encontrada na Focus", nobillingtarget: "Sem faturamento (BT 99)"};
      return (map[d.result] || d.result) + (d.focusStatus ? " (" + d.focusStatus + ")" : "");
    },
    focusStatusClass(result) {
      if (result === "authorized") return "ph-badge-ok";
      if (result === "processing") return "ph-badge-warn";
      return "ph-badge-danger";
    },
    copyText(text) {
      if (!text) return;
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text)
          .then(() => ALERT_SERVICE.swal("Copiado", "Conteúdo copiado.", "success"))
          .catch(() => this.fallbackCopy(text));
      } else {
        this.fallbackCopy(text);
      }
    },
    fallbackCopy(text) {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand("copy");
        ALERT_SERVICE.swal("Copiado", "Conteúdo copiado.", "success");
      } catch (e) {
        ALERT_SERVICE.swal("Aviso", "Não foi possível copiar.", "warning");
      }
      document.body.removeChild(ta);
    },
  },
  created() {
    // Senha (somente front) do dashboard financeiro. Uma vez por sessão.
    if (sessionStorage.getItem("dashFinanceOk") === "1") {
      this.unlocked = true;
      return;
    }
    const pwd = window.prompt("Senha do dashboard financeiro:");
    if (pwd === "dashFINANCE") {
      sessionStorage.setItem("dashFinanceOk", "1");
      this.unlocked = true;
    } else {
      this.unlocked = false;
      this.$router.replace("/dashboard");
    }
  },
  mounted() {
    if (this.unlocked) this.loadDashboard();
  }
};
</script>

<style lang="scss" scoped>
.ph-filterbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  margin-bottom: 10px;
}
.ph-filter {
  position: relative;
}
.ph-filter-btn {
  border: 1px solid #ccd3dc;
  background: #fff;
  border-radius: 6px;
  padding: 4px 10px;
  font-size: 12px;
  cursor: pointer;
  white-space: nowrap;

  &.active {
    border-color: #2e6fdb;
    color: #2e6fdb;
    background: #eef4ff;
  }
}
.ph-filter-pop {
  position: absolute;
  z-index: 20;
  top: 110%;
  left: 0;
  min-width: 200px;
  max-width: 320px;
  background: #fff;
  border: 1px solid #ccd3dc;
  border-radius: 8px;
  box-shadow: 0 6px 20px rgba(0, 0, 0, .12);
  padding: 8px;
}
.ph-filter-pop-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-weight: 600;
  font-size: 12px;
  margin-bottom: 6px;
}
.ph-filter-opts {
  max-height: 240px;
  overflow: auto;
}
.ph-filter-opt {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  padding: 3px 2px;
  cursor: pointer;

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &:hover {
    background: #f4f7fb;
  }
}
.ph-filter-count {
  font-size: 12px;
  color: #6b7686;
  margin-left: auto;
}
.ph-badge-danger {
  display: inline-block;
  background: #fde8e8;
  color: #c0392b;
  border-radius: 4px;
  padding: 1px 7px;
  font-size: 11px;
  font-weight: 600;
}
.ph-muted {
  color: #9aa4b1;
}
.ph-toolbar {
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 8px;
}

.ph-filters label {
  margin-right: 14px;
  font-weight: 600;
  font-size: 13px;
}

.ph-filters select {
  margin-left: 6px;
  padding: 6px 8px;
  border-radius: 4px;
  border: 1px solid #ccc;
}

.ph-check input {
  margin-right: 4px;
}

.ph-triggers .ph-btn {
  margin-left: 8px;
  padding: 8px 12px;
  background-color: #00A651;
  color: #fff;
  border: none;
  border-radius: 4px;
  font-weight: 700;
  cursor: pointer;
}

.ph-btn.small {
  padding: 5px 10px;
  margin-left: 6px;
}

.ph-btn.grey {
  background-color: #888;
}

.ph-btn:disabled {
  background-color: #9bbfa9;
  cursor: not-allowed;
}

.ph-hint {
  font-size: 12px;
  color: #777;
  margin: 4px 0 14px;
}

.ph-cards {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 22px;
}

.ph-card {
  flex: 1 1 15%;
  min-width: 150px;
  background: #fff;
  border-radius: 6px;
  padding: 14px 16px;
  box-shadow: 0 2px 2px 0 rgba(0, 0, 0, .12), 0 0 0 1px rgba(0, 0, 0, .06);
}

.ph-card.clickable {
  cursor: pointer;
}

.ph-card.clickable:hover {
  box-shadow: 0 3px 8px 0 rgba(0, 0, 0, .18);
}

.ph-card-title {
  font-size: 12px;
  color: #555;
  text-transform: uppercase;
  font-weight: 600;
}

.ph-info {
  display: inline-block;
  width: 14px;
  height: 14px;
  line-height: 14px;
  text-align: center;
  border-radius: 50%;
  background: #ddd;
  color: #555;
  font-size: 10px;
}

.ph-card-value {
  font-size: 22px;
  font-weight: 700;
  margin-top: 8px;
}

.ph-charts {
  display: flex;
  flex-wrap: wrap;
  gap: 18px;
  margin-bottom: 24px;
}

.ph-chart {
  flex: 1 1 30%;
  min-width: 260px;
  background: #fff;
  border-radius: 6px;
  padding: 14px;
  box-shadow: 0 2px 2px 0 rgba(0, 0, 0, .10), 0 0 0 1px rgba(0, 0, 0, .05);
}

.ph-chart-title {
  font-weight: 700;
  font-size: 13px;
  margin-bottom: 10px;
}

.ph-bar-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
  cursor: pointer;
}

.ph-bar-label {
  flex: 0 0 110px;
  font-size: 12px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.ph-bar-track {
  flex: 1;
  background: #eee;
  border-radius: 3px;
  height: 12px;
  overflow: hidden;
}

.ph-bar-fill {
  display: block;
  height: 100%;
  background: #00A651;
}

.ph-bar-fill.orange {
  background: #f39c12;
}

.ph-bar-fill.blue {
  background: #2980b9;
}

.ph-bar-val {
  flex: 0 0 90px;
  text-align: right;
  font-size: 12px;
  font-weight: 600;
}

.ph-timeline {
  flex: 1 1 100%;
}

.ph-line {
  width: 100%;
  height: 200px;
  display: block;
}

.ph-line-tall {
  height: 280px;
}

.ph-tl-controls {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 14px;
  margin: 8px 0 6px;
  font-size: 12px;
}

.ph-tl-controls select {
  margin-left: 4px;
}

.ph-tl-loading {
  color: #888;
  font-size: 11px;
}

.ph-collapse {
  cursor: pointer;
  user-select: none;
}

.ph-caret {
  color: #888;
  margin-right: 4px;
}

.ph-grid {
  stroke: #eee;
  stroke-width: 1;
}

.ph-axis {
  fill: #999;
  font-size: 9px;
}

.ph-donut-wrap {
  display: flex;
  align-items: center;
  gap: 12px;
}

.ph-donut {
  width: 120px;
  height: 120px;
  flex: 0 0 120px;
}

.ph-donut-legend {
  font-size: 12px;
}

.ph-legend-item {
  margin-bottom: 4px;
  cursor: pointer;
}

.ph-legend-dot {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  margin-right: 5px;
}

.ph-legend-inline {
  font-size: 11px;
  font-weight: 400;
  color: #666;
  margin-left: 10px;
}

.ph-legend-inline .ph-legend-dot {
  margin-left: 8px;
}

.ph-detail {
  background: #fff;
  border-radius: 6px;
  padding: 14px;
  box-shadow: 0 2px 2px 0 rgba(0, 0, 0, .10), 0 0 0 1px rgba(0, 0, 0, .05);
  min-width: 0;
  max-width: 100%;
}

.ph-detail-body {
  min-width: 0;
  max-width: 100%;
}

.ph-detail-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.ph-table-wrap {
  overflow-x: auto;
  max-width: 100%;
}

.ph-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
}

.ph-table th, .ph-table td {
  border-bottom: 1px solid #eee;
  padding: 7px 9px;
  text-align: left;
  white-space: nowrap;
}

.ph-table th {
  color: #666;
  font-weight: 700;
}

.ph-msg {
  max-width: 380px;
  white-space: normal;
  word-break: break-word;
}

.ph-empty {
  text-align: center;
  color: #999;
  padding: 18px;
}

.link {
  color: #00A651;
  font-weight: 600;
}

/* Seleção / reprocessamento */
.ph-sel-count {
  font-size: 12px;
  font-weight: 600;
  color: #2e6fdb;
  margin-right: 4px;
}

/* Badges de status Focus */
.ph-badge {
  display: inline-block;
  border-radius: 4px;
  padding: 1px 8px;
  font-size: 11px;
  font-weight: 600;
  margin: 0 6px;
}
.ph-badge-ok {
  background: #e6f7ee;
  color: #00A651;
}
.ph-badge-warn {
  background: #fff4e0;
  color: #d68910;
}

/* Modal Focus */
.ph-modal-overlay {
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  z-index: 1050;
  padding: 40px 16px;
  overflow-y: auto;
}
.ph-modal {
  background: #fff;
  border-radius: 8px;
  width: 100%;
  max-width: 760px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
}
.ph-modal-head,
.ph-modal-foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border-bottom: 1px solid #eef0f3;
}
.ph-modal-foot {
  border-bottom: none;
  border-top: 1px solid #eef0f3;
  gap: 8px;
  justify-content: flex-end;
}
.ph-modal-body {
  padding: 12px 16px;
  max-height: 65vh;
  overflow-y: auto;
}
.ph-modal-label {
  display: block;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  color: #7a8694;
  margin: 10px 0 4px;
}
.ph-pre {
  background: #f6f8fa;
  border: 1px solid #e1e6ec;
  border-radius: 6px;
  padding: 8px 10px;
  font-size: 12px;
  white-space: pre-wrap;
  word-break: break-word;
  max-height: 220px;
  overflow-y: auto;
  margin: 0;
}
</style>



// WEBPACK FOOTER //
// src/vue/pages/restricted/healthcheck/paymentHealthcheck.vue